import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import Form from '@product-framework/components/AdvancedForms/Form';
import { getDefaultValuesForForm, IconLibrary } from '@product-framework/index';
import {
  SnackbarApiErrorMessage,
  useEntityPermission,
  useLocalization,
  useSnackbar,
  useUserAccess,
} from '@ui-framework/index';
import { createSchema } from '../../schemas';
import {
  IssueCompanySelector,
  IssueComponentSelect,
  IssuePrioritySelect,
  IssueServiceAdministratorSelector,
  IssueTypeSelect,
} from '../IssueLookupSelect';
import { INITIAL_ISSUE_FORM, ISSUE_API_ROOT } from '../constants';
import QueuedAttachmentList from '../IssueAttachments/QueuedAttachmentList';
import { stageIssueAttachmentUploads } from '../IssueAttachments/api';
import { applyIssueFieldPermissions, stripEmptyValues } from '../utils';
import { CREATE_ISSUE_ACTION_MESSAGE, CREATE_REQUEST_MESSAGE } from './copy';
import validationSchema from './validations';

const FORM_ID = 'ISSUES_CREATE_FORM';

function IssueCreateDialog({
  open,
  onClose,
  onCreated,
  dataProvider,
  issueConfig,
  fieldPermissions = {},
  onDirtyChange = () => undefined,
}) {
  const formRef = useRef();
  const [queuedAttachments, setQueuedAttachments] = useState([]);
  const [creating, setCreating] = useState(false);
  const snackbar = useSnackbar();
  const userAccess = useUserAccess();
  const { formatMessage } = useLocalization();
  const { isHidden } = useEntityPermission();
  const isAdmin = userAccess.isAdmin();

  // When a backend permission map is present, per-field permissions govern
  // the form (permittedByDefault would override them all to WRITE).
  const hasFieldPermissions = Object.keys(fieldPermissions ?? {}).length > 0;

  const schema = useMemo(() => {
    const baseSchema = isAdmin
      ? createSchema
      : createSchema.filter(field => !field.adminOnly);
    // isHidden from useEntityPermission is pure, so the schema only needs to
    // rebuild on role/permission changes (isHidden is intentionally omitted
    // from deps to keep the schema — and thus the form defaults — stable).
    return applyIssueFieldPermissions(baseSchema, fieldPermissions, isHidden);
  }, [isAdmin, fieldPermissions]);

  const defaultValues = useMemo(
    () => ({
      ...getDefaultValuesForForm({ schema }),
      ...INITIAL_ISSUE_FORM,
    }),
    [schema],
  );

  useEffect(() => {
    if (!open) {
      formRef.current?.reset?.();
      setQueuedAttachments([]);
      onDirtyChange(false);
    }
  }, [onDirtyChange, open]);

  useEffect(() => {
    onDirtyChange(queuedAttachments.length > 0);
  }, [onDirtyChange, queuedAttachments.length]);

  const resetAndClose = (reason = 'cancelled') => {
    formRef.current?.reset?.();
    setQueuedAttachments([]);
    onDirtyChange(false);
    window.setTimeout(() => onClose(reason), 0);
  };

  const requestClose = () => {
    const hasUnsavedChanges =
      formRef.current?.isDirty?.() || queuedAttachments.length > 0;

    if (!hasUnsavedChanges) {
      resetAndClose();
      return;
    }

    const actionDialog = formRef.current?.getActionDialog?.();
    actionDialog?.init({
      title: formatMessage({
        id: 'UNSAVED_CHANGES',
        defaultMessage: 'Unsaved Changes',
      }),
      message: formatMessage({
        id: 'ISSUES_CREATE_UNSAVED_CHANGES_MESSAGE',
        defaultMessage:
          'You have unsaved changes. Are you sure you want to close this dialog?',
      }),
      buttons: [
        {
          label: formatMessage({
            id: 'STAY',
            defaultMessage: 'Stay',
          }),
          variant: 'contained',
          onClick: () => actionDialog.close(),
        },
        {
          label: formatMessage({
            id: 'LEAVE',
            defaultMessage: 'Leave',
          }),
          variant: 'contained',
          color: 'secondary',
          onClick: () => {
            actionDialog.close();
            resetAndClose('cancelled');
          },
        },
      ],
    });
    actionDialog?.open();
  };

  const onSubmit = async data => {
    setCreating(true);
    try {
      // Upload attachments BEFORE creating the issue: if any upload fails,
      // no issue is created at all.
      let stagedAttachments = [];
      if (queuedAttachments.length > 0) {
        const uploadResults = await stageIssueAttachmentUploads({
          dataProvider,
          files: queuedAttachments,
        });
        const failedUploads = uploadResults.filter(
          uploadResult => !uploadResult.ok,
        );
        if (failedUploads.length > 0) {
          const uploadErrors = failedUploads.flatMap(uploadResult =>
            uploadResult.errors?.length
              ? uploadResult.errors
              : [
                  {
                    message: formatMessage(
                      {
                        id: 'COULD_NOT_UPLOAD_FILENAME',
                        defaultMessage: 'Could not upload {fileName}',
                      },
                      { fileName: uploadResult.fileName },
                    ),
                  },
                ],
          );
          snackbar.showMessage({
            type: 'error',
            data: formatMessage(
              {
                id: 'ISSUES_CREATE_ATTACHMENT_UPLOAD_ERROR',
                defaultMessage:
                  'Request was not created because {count} attachment(s) could not be uploaded. Please try again.',
              },
              { count: failedUploads.length },
            ),
          });
          return {
            nextValues: data,
            errors: uploadErrors,
          };
        }
        stagedAttachments = uploadResults.map(uploadResult => ({
          uploadId: uploadResult.uploadId,
          fileName: uploadResult.fileName,
          attachmentType: uploadResult.attachmentType || 'OTHER',
        }));
      }

      const payload = {
        ...stripEmptyValues({
          ...data,
          ...(!isAdmin && {
            companyId: undefined,
            serviceAdministratorId: undefined,
          }),
        }),
        ...(stagedAttachments.length > 0 && {
          attachments: stagedAttachments,
        }),
      };
      const result = await dataProvider.post(ISSUE_API_ROOT, payload);

      if (result.ok) {
        const createdIssue = result.data;
        snackbar.showMessage(
          formatMessage({
            id: 'REQUEST_WAS_CREATED_SUCCESSFULLY',
            defaultMessage: 'Request was created successfully',
          }),
        );
        return {
          nextValues: {
            ...getDefaultValuesForForm({ schema }),
            ...INITIAL_ISSUE_FORM,
          },
          errors: null,
          callback: () => {
            setQueuedAttachments([]);
            onCreated(createdIssue);
            resetAndClose('created');
          },
        };
      }

      snackbar.showMessage({
        type: 'error',
        data: <SnackbarApiErrorMessage errors={result?.errors} />,
      });

      return {
        nextValues: data,
        errors: result?.errors,
      };
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onClose={requestClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconLibrary.IssueCreate />
        {formatMessage(CREATE_REQUEST_MESSAGE)}
      </DialogTitle>
      <DialogContent>
        {open && (
          <Box sx={{ mt: 1 }}>
            <Form
              dialog
              permittedByDefault={!hasFieldPermissions}
              formId={FORM_ID}
              ref={formRef}
              schema={schema}
              customComponents={{
                IssueCompanySelector,
                IssueComponentSelect,
                IssuePrioritySelect,
                IssueServiceAdministratorSelector,
                IssueTypeSelect,
              }}
              validationSchema={validationSchema(formatMessage, isAdmin)}
              defaultValues={defaultValues}
              onSubmit={onSubmit}
            />
            <QueuedAttachmentList
              files={queuedAttachments}
              onAddFiles={files =>
                setQueuedAttachments(current => [
                  ...current,
                  ...files.map(file => ({ file, attachmentType: 'OTHER' })),
                ])
              }
              onRemoveFile={index =>
                setQueuedAttachments(current =>
                  current.filter((_, idx) => idx !== index),
                )
              }
              onChangeType={(index, attachmentType) =>
                setQueuedAttachments(current =>
                  current.map((item, idx) =>
                    idx === index ? { ...item, attachmentType } : item,
                  ),
                )
              }
              formatMessage={formatMessage}
              fileExtensions={issueConfig?.attachmentFileExtensions}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions display="flex" sx={{ justifyContent: 'center', my: 2 }}>
        <Button
          size="small"
          variant="contained"
          disabled={creating}
          startIcon={<IconLibrary.IssueCreate />}
          onClick={() => formRef.current?.submit({ formId: FORM_ID })}>
          {formatMessage(CREATE_ISSUE_ACTION_MESSAGE)}
        </Button>
        <Button
          size="small"
          variant="contained"
          color="secondary"
          disabled={creating}
          startIcon={<IconLibrary.Cancel />}
          onClick={requestClose}>
          {formatMessage({
            id: 'CANCEL',
            defaultMessage: 'Cancel',
          })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default IssueCreateDialog;
